import { createContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import { readStoredIconSet, writeStoredIconSet, type IconSetId } from './icons';
import {
  applyResolvedTheme,
  readStoredThemePreference,
  resolveTheme,
  syncThemeColorMeta,
  writeStoredThemePreference,
  type ResolvedTheme,
  type ThemePreference,
} from './theme';

const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

function getSystemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(DARK_MEDIA_QUERY).matches;
}

export interface ThemeContextValue {
  /** The stored preference — may be `'system'`. */
  preference: ThemePreference;
  /** What's actually applied to `data-theme` right now. */
  resolved: ResolvedTheme;
  /** Persists to localStorage and applies immediately. */
  setPreference: (preference: ThemePreference) => void;
  /** Live OS dark-mode signal — lets the switcher preview what "System" currently means. */
  systemPrefersDark: boolean;
  /** Which nav icon set the chrome renders (`awwyeah:icons`). Lives here rather than in its
   * own provider because it's the same kind of thing — a per-device appearance preference
   * with one Settings row group — and every consumer already needs this context. */
  iconSet: IconSetId;
  /** Persists to localStorage and applies immediately. */
  setIconSet: (set: IconSetId) => void;
}

export const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Single source of truth for the active theme, mounted once at the app root (main.tsx) so
 * it's live on every route — including the public login/install pages, not just while
 * Settings is mounted. The pre-paint inline snippet in index.html already applied *some*
 * theme before React hydrated (avoiding a flash of the wrong theme); this provider takes
 * over from there: it re-applies on every preference/system change, keeps the PWA
 * theme-color meta tag in sync, and live-updates when `preference === 'system'` and the OS
 * switches modes.
 */
export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    readStoredThemePreference(window.localStorage),
  );
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark);
  const [iconSet, setIconSetState] = useState<IconSetId>(() =>
    readStoredIconSet(window.localStorage),
  );

  useEffect(() => {
    const mql = window.matchMedia(DARK_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent): void => setSystemPrefersDark(event.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  const resolved = resolveTheme(preference, systemPrefersDark);

  useEffect(() => {
    const root = document.documentElement;
    applyResolvedTheme(root, resolved);
    syncThemeColorMeta(document, root);
  }, [resolved]);

  const setPreference = useCallback((next: ThemePreference) => {
    writeStoredThemePreference(window.localStorage, next);
    setPreferenceState(next);
  }, []);

  const setIconSet = useCallback((next: IconSetId) => {
    writeStoredIconSet(window.localStorage, next);
    setIconSetState(next);
  }, []);

  return (
    <ThemeContext.Provider
      value={{ preference, resolved, setPreference, systemPrefersDark, iconSet, setIconSet }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
